// USED IN SERVER - BE MINDFUL OF IMPORTS

import { addDays, endOfDay, startOfDay, subDays } from "date-fns"

// https://www.gov.uk/bank-holidays
// https://www.gov.uk/bank-holidays#northern-ireland
// https://www.gov.uk/bank-holidays#scotland

const date = 1
const month = 0

export function getResetDateRange() {
  // checking if the date is in the future to adjust the filter accordingly to handle years
  const pastDate = new Date(new Date().getFullYear(), month, date)
  let futureDate = new Date()
  if (pastDate.valueOf() > new Date().valueOf()) {
    // reset date is in future, so we need to subtract a year, and set the "lte" date to the
    // future reset date
    futureDate = new Date(pastDate)
    pastDate.setFullYear(pastDate.getFullYear() - 1)
  } else {
    // reset date is in past (e.g current date is 2nd oct), so we can use that as min date
    // and set the "lte" date to the future reset date
    futureDate = new Date(pastDate)
    futureDate.setFullYear(futureDate.getFullYear() + 1)
  }

  return { pastDate, futureDate }
}

export enum CalendarDateType {
  BankHoliday = "bank_holiday",
  ForcedHoliday = "forced_holiday",
}

export const calendarDateTypes = [
  {
    value: CalendarDateType.BankHoliday,
    label: "Bank Holiday",
    description: "Does not subtract from holiday days",
  },
  {
    value: CalendarDateType.ForcedHoliday,
    label: "Forced Holiday",
    description: "Subtracts from users available holiday days",
  },
]
type IOpts = {
  type?: CalendarDateType
  from?: Date
  to?: Date
  country?: string
  useParsedReset?: boolean
  userId?: string
}

export async function loadHolidayDates(skeem: typeof import("../utils/skeem").skeem, opts?: IOpts) {
  const dates = await loadHolidayRecords(skeem, opts)
  return dates?.map(x => x.date) ?? []
}

export async function loadHolidayRecords(
  skeem: typeof import("../utils/skeem").skeem,
  opts?: IOpts,
) {
  let filter = { matches: {} } as any
  let country = opts?.country
  if (opts?.userId && !country) {
    const user = await skeem.fetch2<{ settings?: { hrRegion?: string } }>({
      first: {
        name: "skeem_users",
        attributes: [{ as: "settings", value: { path: ["baseBranch", "settings"] } }],
        filter: { matches: { id: opts?.userId } },
      },
    })
    country = user?.settings?.hrRegion
  }
  if (country) {
    filter.matches.country = country
  }
  const dateRangeFilter = []
  if (opts?.from) {
    dateRangeFilter.push({ matches: { date: { gte: opts.from } } })
  }
  if (opts?.to) {
    dateRangeFilter.push({ matches: { date: { lte: opts.to } } })
  }
  if (opts?.type) {
    filter.matches.type = opts.type
  }
  if (opts?.useParsedReset) {
    const { pastDate, futureDate } = getResetDateRange()
    const fullFilter = {
      and: [
        // lazy deep clone
        JSON.parse(JSON.stringify(filter)),
        ...dateRangeFilter,
        { matches: { date: { gte: pastDate } } },
        { matches: { date: { lte: futureDate } } },
      ],
    }
    filter = fullFilter
  }
  const hasFilter = "and" in filter || Object.keys(filter.matches).length > 0
  const dates = await skeem.fetch2<
    { id: string; date: Date; country: string; type: CalendarDateType }[]
  >({
    model: {
      name: "calendar_dates",
      attributes: ["date", "country", "type"],
      filter: hasFilter ? filter : undefined,
    },
  })
  return dates.map(x => ({ ...x, date: new Date(x.date) }))
}

export async function loadBankHolidays(skeem: any, opts?: { from?: Date; country?: string }) {
  const dates = await loadBankHolidayRecords(skeem, opts)
  return dates.map(x => x.date)
}

export async function loadBankHolidayRecords(skeem: any, opts?: { from?: Date; country?: string }) {
  const filter = { matches: { type: CalendarDateType.BankHoliday } as any }
  if (opts?.country) {
    filter.matches.country = opts.country
  }
  if (opts?.from) {
    filter.matches.date = { gte: opts.from }
  }
  const dates = (await skeem.fetch2({
    model: {
      name: "calendar_dates",
      attributes: ["date", "country"],
      filter: filter,
    },
  })) as { date: Date; country: string }[]
  return dates.map(x => ({ ...x, date: new Date(x.date) }))
}

export async function loadDeductedHolidays(
  skeem: any,
  opts?: { from?: Date; country?: string; useParsedReset?: boolean },
) {
  let filter = { matches: { type: CalendarDateType.ForcedHoliday } } as any
  if (opts?.country) {
    filter.matches.country = opts.country
  }
  if (opts?.from) {
    filter.matches.date = { gte: opts.from }
  }
  if (opts?.useParsedReset) {
    const { pastDate, futureDate } = getResetDateRange()
    const fullFilter = {
      and: [
        JSON.parse(JSON.stringify(filter)),
        { matches: { date: { gte: pastDate } } },
        { matches: { date: { lte: futureDate } } },
      ],
    }
    filter = fullFilter
  }
  const dates = (await skeem.fetch2({
    model: {
      name: "calendar_dates",
      attributes: ["date", "country"],
      filter: filter,
    },
  })) as { date: Date; country: string }[]
  return dates.map(x => ({ ...x, date: new Date(x.date) }))
}

export function isBankHoliday(date: Date, dates: Date[]) {
  return dates.some(
    bankHoliday =>
      bankHoliday.getDate() === date.getDate() &&
      bankHoliday.getMonth() === date.getMonth() &&
      bankHoliday.getFullYear() === date.getFullYear(),
  )
}

export function bankHolidaysInRange(startDate: Date, endDate: Date, dates: Date[]) {
  return dates.filter(
    date => startOfDay(date) >= startOfDay(startDate) && endOfDay(date) <= endOfDay(endDate),
  )
}

export function isDateInArray(targetDate: Date, dateArray: Date[]) {
  // Normalize the target to midnight local time
  const t = new Date(targetDate)
  t.setHours(0, 0, 0, 0)
  const tMs = t.getTime()

  return dateArray.some(datum => {
    const d = new Date(datum)
    d.setHours(0, 0, 0, 0)
    return d.getTime() === tMs
  })
}

export function bankHolidaysInMonth(date: Date, bankHolidays: Date[]) {
  return bankHolidays.filter(
    bankHoliday =>
      bankHoliday.getMonth() === date.getMonth() &&
      bankHoliday.getFullYear() === date.getFullYear(),
  )
}

export function firstWorkingDayOfWeek(date: Date, dates: Date[]) {
  let newDate = startOfDay(new Date(date))
  let iteration = 0
  while ((isBankHoliday(newDate, dates) || [0, 6].includes(newDate.getDay())) && iteration < 7) {
    newDate = addDays(newDate, 1)
    iteration++
  }
  if (iteration >= 7) {
    throw new Error("Could not find last working day of week")
  }
  return newDate
}

export function lastWorkingDayOfWeek(date: Date, dates: Date[]) {
  let newDate = startOfDay(new Date(date))
  let iteration = 0
  while ((isBankHoliday(newDate, dates) || [0, 6].includes(newDate.getDay())) && iteration < 7) {
    newDate = subDays(newDate, 1)
    iteration++
  }
  if (iteration >= 7) {
    throw new Error("Could not find last working day of week")
  }
  return newDate
}

// months = [
//   "January",
//   "February",
//   "march",
//   "April",
//   "May",
//   "June",
//   "July",
//   "August",
//   "September",
//   "October",
//   "November",
//   "December",
// ].map((v) => v.toLowerCase())
// bankHolidays = [
//   "27 December 22",
// "26 December 22",
// "19 September 22",
// "29 August 22",
// "3 June 22",
// "2 June 22",
// "2 May 22",
// "18 April 22",
// "15 April 22",
// "3 January 22",
// "28 December 21",
// "27 December 21",
// "30 August 21",
// "31 May 21",
// "3 May 21",
// "5 April 21",
// "2 April 21",
// "1 January 21",
// "28 December 20",
// "25 December 20",
// "31 August 20",
// "25 May 20",
// "8 May 20",
// "13 April 20",
// "10 April 20",
// "1 January 20",
// "26 December 19",
// "25 December 19",
// "26 August 19",
// "27 May 19",
// "6 May 19",
// "22 April 19",
// "19 April 19",
// "1 January 19",
// "26 December 18",
// "25 December 18",
// "27 August 18",
// "28 May 18",
// "7 May 18",
// "2 April 18",
// "30 March 18",
// "1 January 18",
//   "10 April 23",
//   "7 April 23",
//   "2 January 23",
//   "1 May 23",
//   "8 May 23",
//   "29 May 23",
//   "28 August 23",
//   "25 December 23",
//   "26 December 23",
//   "1 January 24",
//   "29 March 24",
//   "1 April 24",
//   "6 May 24",
//   "27 May 24",
//   "26 August 24",
//   "25 December 24",
//   "26 December 24",
//   "1 January 25",
//   "18 April 25",
//   "21 April 25",
//   "5 May 25",
//   "26 May 25",
//   "25 August 25",
//   "25 December 25",
//   "26 December 25",
// ].map((dateStr) => {
//   const [date, monthName, yearPart] = dateStr.split(" ")
//   const month = months.indexOf(monthName.toLowerCase())
//   const datestrv = `${date.padStart(2, "0")}/${(Number(month) + 1)
//     .toString()
//     .padStart(2, "0")}/20${yearPart}`

//   return `20${yearPart}/${(Number(month) + 1).toString().padStart(2, "0")}/${date.padStart(2, "0")}`
// })
